import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, isBefore,
} from "date-fns";
import { fr } from "date-fns/locale";
import { PhoneCall, CheckCircle2, ChevronLeft, ChevronRight, CalendarDays, CalendarClock, XCircle, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const WEEK_DAYS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

export default function BookingManage() {
  const { token, manage } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(null); // null | "reschedule" | "cancel"
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null); // "rescheduled" | "cancelled"

  useEffect(() => {
    api.get(`/booking/public/${token}/manage/${manage}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.status === 410
        ? "Les réservations en ligne sont temporairement désactivées."
        : "Ce lien de gestion n'est plus valide : la réservation a été annulée ou supprimée. Si vous avez reçu plusieurs emails de confirmation, utilisez le lien de l'email le plus récent."));
  }, [token, manage]);

  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const maxDay = useMemo(() => addDays(today, info?.max_days_ahead ?? 30), [today, info]);

  const pickDay = async (d) => {
    setDay(d); setSlot(""); setSlots(null);
    try {
      const { data } = await api.get(`/booking/public/${token}/slots`, { params: { day: format(d, "yyyy-MM-dd") } });
      setSlots(data.slots);
    } catch { setSlots([]); }
  };

  const reschedule = async () => {
    setSending(true);
    try {
      await api.post(`/booking/public/${token}/manage/${manage}/reschedule`, {
        date: format(day, "yyyy-MM-dd"), start_time: slot,
      });
      setDone("rescheduled");
    } catch (e) {
      toast.error(apiError(e));
      if (e?.response?.status === 409) pickDay(day);
    } finally { setSending(false); }
  };

  const cancel = async () => {
    setSending(true);
    try {
      await api.post(`/booking/public/${token}/manage/${manage}/cancel`, { reason });
      setDone("cancelled");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const brand = info?.brand_color || "#0f172a";
  const inputCls = "w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-400 transition-colors";

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="manage-error">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-10 text-center max-w-md">
          <CalendarDays className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
          <p className="font-semibold text-neutral-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  const rdvDate = new Date(info.date + "T12:00:00");
  const recap = (
    <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-4 flex items-center gap-3" data-testid="manage-recap">
      <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${brand}1a` }}>
        <PhoneCall className="w-5 h-5" style={{ color: brand }} />
      </span>
      <div>
        <p className="text-sm font-semibold text-neutral-900 capitalize">
          {format(rdvDate, "EEEE d MMMM yyyy", { locale: fr })} · {info.start_time}
        </p>
        <p className="text-xs text-neutral-500">{info.type_label} · {info.duration} min · avec {info.app_name}</p>
      </div>
    </div>
  );

  if (done) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="manage-success">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-10 text-center max-w-md w-full">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: `${brand}1a` }}>
            <CheckCircle2 className="w-7 h-7" style={{ color: brand }} />
          </div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900">
            {done === "rescheduled" ? "Demande de report envoyée" : "Demande d'annulation envoyée"}
          </h1>
          <p className="text-neutral-500 mt-2 text-sm">
            {done === "rescheduled"
              ? `${info.app_name} a été notifié et vous reconfirmera rapidement votre nouveau créneau.`
              : `${info.app_name} a été notifié de votre demande d'annulation.`}
          </p>
        </div>
      </div>
    );
  }

  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const days = [];
  for (let d = gridStart; !isBefore(gridEnd, d); d = addDays(d, 1)) days.push(d);

  return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4" data-testid="manage-page">
      <div className="max-w-lg mx-auto">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-neutral-100 flex items-center gap-3">
            {info.app_logo_url ? (
              <img src={info.app_logo_url} alt={info.app_name} className="w-10 h-10 rounded-xl object-cover border border-neutral-200" />
            ) : (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: brand }}>
                {(info.app_name || "S")[0]}
              </div>
            )}
            <div>
              <h1 className="font-heading text-lg font-bold text-neutral-900" data-testid="manage-title">Gérer mon rendez-vous</h1>
              <p className="text-xs text-neutral-500">{info.app_name}</p>
            </div>
          </div>

          <div className="p-6 space-y-4">
            {recap}

            {info.status === "a_reconfirmer" && (
              <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2.5" data-testid="manage-status-pending">
                <AlertTriangle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                <p className="text-xs text-orange-800">Votre demande de report est en cours — {info.app_name} vous reconfirmera ce créneau.</p>
              </div>
            )}
            {info.status === "annulation_demandee" && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" data-testid="manage-status-cancel">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <p className="text-xs text-red-800">Votre demande d'annulation a bien été transmise à {info.app_name}.</p>
              </div>
            )}
            {info.status === "annule" && (
              <div className="flex items-start gap-2 bg-neutral-100 border border-neutral-200 rounded-lg px-3 py-2.5" data-testid="manage-status-cancelled">
                <XCircle className="w-4 h-4 text-neutral-500 shrink-0 mt-0.5" />
                <p className="text-xs text-neutral-600">Ce rendez-vous a été annulé.</p>
              </div>
            )}

            {!mode && info.status !== "annule" && info.status !== "annulation_demandee" && (
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="manage-reschedule-btn" onClick={() => setMode("reschedule")}
                  className="flex items-center justify-center gap-2 text-white rounded-lg py-2.5 text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ background: brand }}>
                  <CalendarClock className="w-4 h-4" /> Reprogrammer
                </button>
                <button data-testid="manage-cancel-btn" onClick={() => setMode("cancel")}
                  className="flex items-center justify-center gap-2 border border-red-200 text-red-600 rounded-lg py-2.5 text-sm font-semibold hover:bg-red-50 transition-colors">
                  <XCircle className="w-4 h-4" /> Annuler
                </button>
              </div>
            )}

            {mode === "reschedule" && (
              <div className="space-y-3" data-testid="manage-reschedule">
                <p className="text-sm font-semibold text-neutral-800">Choisissez un nouveau créneau</p>
                <div className="border border-neutral-200 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <button data-testid="manage-prev-month" disabled={isSameMonth(month, today)}
                      onClick={() => setMonth((m) => subMonths(m, 1))}
                      className="p-1.5 rounded-lg hover:bg-neutral-100 disabled:opacity-30 transition-colors">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <p className="text-sm font-semibold capitalize">{format(month, "MMMM yyyy", { locale: fr })}</p>
                    <button data-testid="manage-next-month" disabled={isSameMonth(month, maxDay)}
                      onClick={() => setMonth((m) => addMonths(m, 1))}
                      className="p-1.5 rounded-lg hover:bg-neutral-100 disabled:opacity-30 transition-colors">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center">
                    {WEEK_DAYS.map((d) => <span key={d} className="text-[11px] font-medium text-neutral-400 py-1">{d}</span>)}
                    {days.map((d) => {
                      const disabled = !isSameMonth(d, month) || isBefore(d, today) || isBefore(maxDay, d);
                      const active = day && isSameDay(d, day);
                      return (
                        <button key={d.toISOString()} data-testid={disabled ? undefined : `manage-day-${format(d, "yyyy-MM-dd")}`}
                          disabled={disabled} onClick={() => pickDay(d)}
                          className={`aspect-square rounded-lg text-sm transition-colors ${
                            active ? "text-white font-semibold" : disabled ? "text-neutral-300 cursor-not-allowed" : "hover:bg-neutral-100 text-neutral-700"
                          }`}
                          style={active ? { background: brand } : undefined}>
                          {format(d, "d")}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {day && slots === null && (
                  <div className="py-6 flex justify-center"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>
                )}
                {day && slots !== null && (
                  slots.length === 0 ? (
                    <p className="text-sm text-neutral-400" data-testid="manage-no-slots">Aucun créneau disponible ce jour-là.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1" data-testid="manage-slots">
                      {slots.map((s) => (
                        <button key={s} data-testid={`manage-slot-${s.replace(":", "h")}`} onClick={() => setSlot(s)}
                          className={`border rounded-lg py-2 text-sm font-medium transition-colors ${slot === s ? "text-white" : "border-neutral-200 hover:bg-neutral-100"}`}
                          style={slot === s ? { background: brand, borderColor: brand } : undefined}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )
                )}
                <div className="flex gap-2">
                  <button data-testid="manage-reschedule-confirm" onClick={reschedule} disabled={!slot || sending}
                    className="flex-1 text-white rounded-lg py-2.5 text-sm font-semibold disabled:opacity-50 transition-opacity"
                    style={{ background: brand }}>
                    {sending ? "Envoi…" : "Confirmer le nouveau créneau"}
                  </button>
                  <button onClick={() => { setMode(null); setDay(null); setSlot(""); }}
                    className="px-4 border border-neutral-200 rounded-lg text-sm text-neutral-600 hover:bg-neutral-50 transition-colors">Retour</button>
                </div>
                <p className="text-[11px] text-neutral-400 text-center">Le nouveau créneau sera soumis à reconfirmation par {info.app_name}.</p>
              </div>
            )}

            {mode === "cancel" && (
              <div className="space-y-3" data-testid="manage-cancel">
                <p className="text-sm font-semibold text-neutral-800">Annuler ce rendez-vous ?</p>
                <textarea data-testid="manage-cancel-reason" rows={3} className={`${inputCls} resize-none`}
                  placeholder="Motif (facultatif) — ex : empêchement, je préfère une autre date…"
                  value={reason} onChange={(e) => setReason(e.target.value)} />
                <div className="flex gap-2">
                  <button data-testid="manage-cancel-confirm" onClick={cancel} disabled={sending}
                    className="flex-1 bg-red-600 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50">
                    {sending ? "Envoi…" : "Confirmer l'annulation"}
                  </button>
                  <button onClick={() => setMode(null)}
                    className="px-4 border border-neutral-200 rounded-lg text-sm text-neutral-600 hover:bg-neutral-50 transition-colors">Retour</button>
                </div>
                <p className="text-[11px] text-neutral-400 text-center">{info.app_name} sera immédiatement notifié de votre demande.</p>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-neutral-400 mt-4">Propulsé par DGA</p>
      </div>
    </div>
  );
}
